import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Lock as LockIcon,
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
  TrendingFlat as FlatIcon,
} from '@mui/icons-material';

// Frequency options with multipliers from monthly
const FREQUENCY_OPTIONS = {
  weekly: { label: 'Weekly', multiplier: 12 / 52, suffix: '/wk' },
  biweekly: { label: 'Bi-weekly', multiplier: 12 / 26, suffix: '/2wk' },
  monthly: { label: 'Monthly', multiplier: 1, suffix: '/mo' },
};

function ScenarioComparison({ scenarioResult, calculating }) {
  const [frequency, setFrequency] = useState('monthly');

  const currentFreq = FREQUENCY_OPTIONS[frequency];

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const adjusted = amount * currentFreq.multiplier;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(adjusted);
  };

  const formatWithSuffix = (amount) => {
    return `${formatCurrency(amount)}${currentFreq.suffix}`;
  };

  const getDeltaIcon = (delta) => {
    if (delta > 0) return <UpIcon fontSize="small" color="success" />;
    if (delta < 0) return <DownIcon fontSize="small" color="error" />;
    return <FlatIcon fontSize="small" color="action" />;
  };

  const getDeltaColor = (delta) => {
    if (delta > 0) return 'success.main';
    if (delta < 0) return 'error.main';
    return 'text.secondary';
  };

  if (calculating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!scenarioResult) {
    return (
      <Alert severity="info">
        No scenario data available. Please complete the previous steps.
      </Alert>
    );
  }

  const { baseline, scenario, delta, allocations, templates_missing_defaults } = scenarioResult;

  // Combine locked and flexible for display
  const allCategories = [
    ...(allocations?.locked || []).map(c => ({ ...c, isLocked: true })),
    ...(allocations?.flexible || []).map(c => ({ ...c, isLocked: false })),
  ];

  // Group by account
  const categoriesByAccount = allCategories.reduce((acc, cat) => {
    const accountName = cat.account_name;
    if (!acc[accountName]) {
      acc[accountName] = [];
    }
    acc[accountName].push(cat);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Scenario Summary
        </Typography>
        <ToggleButtonGroup
          value={frequency}
          exclusive
          onChange={(e, val) => val && setFrequency(val)}
          size="small"
        >
          {Object.entries(FREQUENCY_OPTIONS).map(([key, { label }]) => (
            <ToggleButton key={key} value={key}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Template Reality Check - only show if NOT using fallback and there's a meaningful difference */}
      {baseline?.net_monthly && allocations?.totals?.current_total && !allocations?.using_calculated_fallback && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            Template Reality Check
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Calculated Take-Home</Typography>
              <Typography variant="body1">{formatWithSuffix(baseline?.net_monthly)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Your Template Allocations</Typography>
              <Typography variant="body1">{formatWithSuffix(allocations?.totals?.current_total)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Difference</Typography>
              <Typography
                variant="body1"
                sx={{
                  color: Math.abs(allocations?.totals?.current_total - baseline?.net_monthly) > 100
                    ? 'warning.dark'
                    : 'success.dark'
                }}
              >
                {formatCurrency(allocations?.totals?.current_total - baseline?.net_monthly)}
              </Typography>
            </Grid>
          </Grid>
          {Math.abs(allocations?.totals?.current_total - baseline?.net_monthly) > 100 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Your template allocations differ from calculated take-home. This may be due to C2C tax handling,
              deductions, or templates needing adjustment.
            </Typography>
          )}
        </Paper>
      )}

      {/* No Templates Notice */}
      {allocations?.using_calculated_fallback && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No split templates defined. Using calculated take-home based on your income and state taxes.
          For more accurate results, set up split templates to define your actual allocations.
        </Alert>
      )}

      {/* Missing Default Amounts Warning */}
      {templates_missing_defaults?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Some templates need attention:</strong> The following income templates use percentage-based
          allocations but don't have a default amount set. Their allocations are not included in this calculation.
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {templates_missing_defaults.map((t, idx) => (
              <li key={idx}>{t.template_name} ({t.account_name})</li>
            ))}
          </ul>
          Go to Split Templates and set the "Default Amount" for these templates.
        </Alert>
      )}

      {/* High-Level Comparison */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              CURRENT ({baseline?.state})
            </Typography>
            <Typography variant="h4" gutterBottom>
              {formatWithSuffix(allocations?.totals?.current_total)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Based on your template allocations
            </Typography>
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              {getDeltaIcon(delta?.total)}
              <Typography
                variant="h6"
                sx={{ color: getDeltaColor(delta?.total) }}
              >
                {delta?.total >= 0 ? '+' : ''}{formatCurrency(delta?.total)}
              </Typography>
              {scenario?.tax_adjustment_factor && scenario?.tax_adjustment_factor !== 1 && (
                <Typography variant="caption" color="text.secondary">
                  Tax Factor: {(scenario.tax_adjustment_factor * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              SCENARIO ({scenario?.state})
            </Typography>
            <Typography variant="h4" gutterBottom>
              {formatWithSuffix(allocations?.totals?.scenario_total)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After tax adjustment
              {scenario?.factors_net !== 0 && (
                <> and factors ({formatWithSuffix(scenario?.factors_net)})</>
              )}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {delta?.from_state_change !== 0 && (
            <Chip
              label={`State change: ${delta?.from_state_change >= 0 ? '+' : ''}${formatWithSuffix(delta?.from_state_change)}`}
              color={delta?.from_state_change >= 0 ? 'success' : 'error'}
              size="small"
            />
          )}
          {delta?.from_factors !== 0 && (
            <Chip
              label={`Factors: ${delta?.from_factors >= 0 ? '+' : ''}${formatWithSuffix(delta?.from_factors)}`}
              color={delta?.from_factors >= 0 ? 'success' : 'error'}
              size="small"
            />
          )}
        </Box>
      </Paper>

      {/* Factors Breakdown */}
      {scenario?.factors?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Scenario Factors</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {scenario.factors.map((factor, idx) => (
              <Chip
                key={idx}
                label={`${factor.label || factor.factor_type}: ${factor.factor_type === 'income' ? '+' : '-'}${formatCurrency(factor.monthly_amount)}`}
                color={factor.factor_type === 'income' ? 'success' : 'error'}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Detailed Allocation Comparison */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
        Allocation Breakdown
      </Typography>

      {Object.entries(categoriesByAccount).map(([accountName, categories]) => (
        <Paper key={accountName} variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2">{accountName}</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Scenario</TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories
                  .filter(cat => cat.current_allocation > 0 || cat.scenario_allocation > 0)
                  .map(cat => {
                  const catDelta = (cat.scenario_allocation || 0) - (cat.current_allocation || 0);
                  return (
                    <TableRow key={cat.category_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat.category_name}
                          {cat.isLocked && <LockIcon fontSize="small" color="action" />}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(cat.current_allocation)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(cat.scenario_allocation)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: getDeltaColor(catDelta) }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {getDeltaIcon(catDelta)}
                          {catDelta >= 0 ? '+' : ''}{formatCurrency(catDelta)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {/* Summary Footer */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: allocations?.is_balanced ? 'success.light' : 'warning.light' }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2">Current Total</Typography>
            <Typography variant="h6">{formatWithSuffix(allocations?.totals?.current_total)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2">Scenario Total</Typography>
            <Typography variant="h6">{formatWithSuffix(allocations?.totals?.scenario_total)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2">Difference</Typography>
            <Typography variant="h6" sx={{ color: getDeltaColor(delta?.total) }}>
              {delta?.total >= 0 ? '+' : ''}{formatWithSuffix(delta?.total)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2">Status</Typography>
            <Typography variant="h6">
              {allocations?.is_balanced ? 'Balanced' : 'Unbalanced'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Insight */}
      <Alert
        severity={delta?.total >= 0 ? 'success' : 'warning'}
        sx={{ mt: 3 }}
      >
        {delta?.total >= 0 ? (
          <>
            <strong>Good news!</strong> This scenario would give you an extra {formatWithSuffix(Math.abs(delta?.total))} to allocate across your categories.
          </>
        ) : (
          <>
            <strong>Heads up:</strong> This scenario would reduce your budget by {formatWithSuffix(Math.abs(delta?.total))}.
            Review the allocation changes above to see where the impact lands.
          </>
        )}
      </Alert>
    </Box>
  );
}

export default ScenarioComparison;
